import { atom } from 'jotai';
import type { BlockWithRevisions, Task } from '@/types';

export type PanelId =
  | 'quickAdd'
  | 'quickAddTask'
  | 'blockDetail'
  | 'tasks'
  | 'settings'
  | 'quickCapture'
  | 'inbox';

export interface PanelPosition {
  x: number;
  y: number;
}

export interface PanelSize {
  width: number;
  height: number;
}

export interface PanelState {
  isOpen: boolean;
  isMinimized: boolean;
  isMaximized: boolean;
  position: PanelPosition;
  size: PanelSize;
  zIndex: number;
}

export type PanelsState = Record<PanelId, PanelState>;

const STORAGE_KEY = 'timeblock.panels';
const Z_MIN = 400;
const Z_MAX = 500;

const DEFAULT_PANELS: PanelsState = {
  quickAdd: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 400, y: 100 },
    size: { width: 360, height: 520 },
    zIndex: 400,
  },
  quickAddTask: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 420, y: 120 },
    size: { width: 360, height: 480 },
    zIndex: 404,
  },
  blockDetail: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 500, y: 150 },
    size: { width: 420, height: 620 },
    zIndex: 401,
  },
  tasks: {
    isOpen: true,
    isMinimized: false,
    isMaximized: false,
    position: { x: 70, y: 100 },
    size: { width: 300, height: 560 },
    zIndex: 402,
  },
  settings: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 300, y: 80 },
    size: { width: 520, height: 640 },
    zIndex: 403,
  },
  quickCapture: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 350, y: 150 },
    size: { width: 360, height: 300 },
    zIndex: 405,
  },
  inbox: {
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    position: { x: 380, y: 100 },
    size: { width: 380, height: 520 },
    zIndex: 406,
  },
};

const loadPanels = (): PanelsState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      if (window.innerWidth < 768) {
        return Object.fromEntries(
          Object.entries(DEFAULT_PANELS).map(([key, panel]) => [key, { ...panel, isOpen: false }])
        ) as PanelsState;
      }
      return DEFAULT_PANELS;
    }
    const parsed = JSON.parse(raw) as Partial<PanelsState>;
    const merged = {
      ...DEFAULT_PANELS,
      ...parsed,
    } as PanelsState;
    if (window.innerWidth < 768) {
      return Object.fromEntries(
        Object.entries(merged).map(([key, panel]) => [key, { ...panel, isOpen: false }])
      ) as PanelsState;
    }
    return merged;
  } catch {
    return DEFAULT_PANELS;
  }
};

const persistPanels = (state: PanelsState) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore persistence errors (private mode, quota, etc.)
  }
};

const basePanelsAtom = atom<PanelsState>(DEFAULT_PANELS);
basePanelsAtom.onMount = (set) => {
  set(loadPanels());
};

export const panelsAtom = atom(
  (get) => get(basePanelsAtom),
  (get, set, updater: (prev: PanelsState) => PanelsState) => {
    const next = updater(get(basePanelsAtom));
    set(basePanelsAtom, next);
    persistPanels(next);
  }
);

const getNextZ = (panels: PanelsState) => {
  const maxZ = Math.max(...Object.values(panels).map((panel) => panel.zIndex));
  return Math.min(Z_MAX, Math.max(Z_MIN, maxZ + 1));
};

export const openPanelAtom = atom(null, (get, set, panelId: PanelId) => {
  set(panelsAtom, (prev) => {
    const nextZ = getNextZ(prev);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const nextPanels = isMobile
      ? (Object.fromEntries(
          Object.entries(prev).map(([key, panel]) => [
            key,
            { ...panel, isOpen: false, isMinimized: false },
          ])
        ) as PanelsState)
      : prev;
    return {
      ...nextPanels,
      [panelId]: {
        ...nextPanels[panelId],
        isOpen: true,
        isMinimized: false,
        zIndex: nextZ,
      },
    };
  });
});

export const closePanelAtom = atom(null, (get, set, panelId: PanelId) => {
  set(panelsAtom, (prev) => ({
    ...prev,
    [panelId]: {
      ...prev[panelId],
      isOpen: false,
      isMinimized: false,
      isMaximized: false,
    },
  }));
});

export const togglePanelAtom = atom(null, (get, set, panelId: PanelId) => {
  const panel = get(panelsAtom)[panelId];
  if (panel.isOpen) {
    set(closePanelAtom, panelId);
  } else {
    set(openPanelAtom, panelId);
  }
});

export const bringPanelToFrontAtom = atom(null, (get, set, panelId: PanelId) => {
  set(panelsAtom, (prev) => ({
    ...prev,
    [panelId]: {
      ...prev[panelId],
      zIndex: getNextZ(prev),
    },
  }));
});

export const updatePanelAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      id: PanelId;
      position?: PanelPosition;
      size?: PanelSize;
      isMinimized?: boolean;
      isMaximized?: boolean;
    }
  ) => {
    set(panelsAtom, (prev) => ({
      ...prev,
      [payload.id]: {
        ...prev[payload.id],
        position: payload.position ?? prev[payload.id].position,
        size: payload.size ?? prev[payload.id].size,
        isMinimized: payload.isMinimized ?? prev[payload.id].isMinimized,
        isMaximized: payload.isMaximized ?? prev[payload.id].isMaximized,
      },
    }));
  }
);

// Close all panels (used when logging out)
export const closeAllPanelsAtom = atom(null, (get, set) => {
  set(
    panelsAtom,
    (prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, panel]) => [
          key,
          { ...panel, isOpen: false, isMinimized: false, isMaximized: false },
        ])
      ) as PanelsState
  );
});

export const selectedBlockAtom = atom<BlockWithRevisions | null>(null);
export const quickAddStartTimeAtom = atom<number | undefined>(undefined);
export const quickAddDateAtom = atom<string | undefined>(undefined);
export const schedulingTaskAtom = atom<Task | null>(null);
